import { useQueryClient } from '@tanstack/react-query';
import { CATEGORY_ICON_LABELS, CATEGORY_ICONS, categorySchema, type CategoryDTO, type CategoryIcon } from '@bifeai/shared';
import { Button, Callout, Dialog, Input, Select, useToast } from '@bifeai/ui';
import { Archive, Check, Pencil, Plus, X } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { CategoryGlyph } from '../../components/inventory';
import { api, errorMessage } from '../../lib/api';
import { invalidateInventory } from '../../lib/catalog';

export function CategoriesDialog({ categories, onClose }: { categories: CategoryDTO[]; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [icon, setIcon] = useState<CategoryIcon>('beef');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function startEdit(c: CategoryDTO | null) {
    setError(null);
    setEditing(c?.id ?? 'new');
    setName(c?.name ?? '');
    setIcon(c?.icon ?? 'beef');
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    const parsed = categorySchema.safeParse({ name, icon });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Revisá el nombre.');
    setBusy(true);
    setError(null);
    try {
      if (editing === 'new') await api.post('/categories', parsed.data);
      else await api.patch(`/categories/${editing}`, parsed.data);
      setEditing(null);
      await invalidateInventory(qc);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function archive(c: CategoryDTO) {
    setBusy(true);
    try {
      await api.del(`/categories/${c.id}`);
      toast.success('Categoría archivada', c.products > 0 ? 'Sus productos quedaron sin categoría.' : undefined);
      await invalidateInventory(qc);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const editor = (
    <form
      onSubmit={save}
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        flexWrap: 'wrap',
        width: '100%',
      }}
    >
      <Select value={icon} onChange={(e) => setIcon(e.target.value as CategoryIcon)} aria-label="Ícono" style={{ width: 190 }}>
        {CATEGORY_ICONS.map((i) => (
          <option key={i} value={i}>
            {CATEGORY_ICON_LABELS[i]}
          </option>
        ))}
      </Select>
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Nombre"
        aria-label="Nombre"
        autoFocus
        style={{ flex: 1, minWidth: 140 }}
      />
      <Button type="submit" size="sm" variant="primary" iconOnly icon={<Check />} loading={busy}>
        Guardar
      </Button>
      <Button size="sm" variant="ghost" iconOnly icon={<X />} onClick={() => setEditing(null)}>
        Cancelar
      </Button>
    </form>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Categorías" description="Agrupan los productos en la grilla y en el punto de venta.">
      <div className="bf-form">
        {error && <Callout tone="danger">{error}</Callout>}
        <div className="bf-scroll-box">
          <table className="bf-table">
            <tbody>
              {categories.map((c) => (
                <tr key={c.id}>
                  {editing === c.id ? (
                    <td colSpan={3}>{editor}</td>
                  ) : (
                    <>
                      <td style={{ width: 52 }}>
                        <span className="bf-thumb bf-thumb--sm">
                          <CategoryGlyph icon={c.icon} />
                        </span>
                      </td>
                      <td>
                        <span className="bf-cell-main">{c.name}</span>
                        <div className="bf-cell-sub">
                          {c.products} {c.products === 1 ? 'producto' : 'productos'}
                        </div>
                      </td>
                      <td className="bf-cell-actions">
                        <Button size="sm" variant="ghost" iconOnly icon={<Pencil />} onClick={() => startEdit(c)}>
                          Editar {c.name}
                        </Button>
                        <Button size="sm" variant="ghost" iconOnly icon={<Archive />} onClick={() => void archive(c)} disabled={busy}>
                          Archivar {c.name}
                        </Button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {editing === 'new' && (
                <tr>
                  <td colSpan={3}>{editor}</td>
                </tr>
              )}
              {categories.length === 0 && editing !== 'new' && (
                <tr>
                  <td className="bf-cell-sub">Todavía no hay categorías.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="bf-form-actions">
          {editing !== 'new' && (
            <Button icon={<Plus />} onClick={() => startEdit(null)} style={{ marginRight: 'auto' }}>
              Nueva categoría
            </Button>
          )}
          <Button variant="primary" onClick={onClose}>
            Listo
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
